import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, DatePicker, Input, InputNumber, Select, Space, Table, Tabs, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import recognitionService, { CONTRACT_STATUSES, LINE_STATUSES } from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useProjectsForSelection } from '@/hooks/useProject';
import { downloadCsv, downloadXlsx } from '@/reports/utils/exportTable';
import type { ReportColumnDef } from '@/reports/types';
import { StatusTag, accountLabel, amount, apiErrorMessage, contractPath, decimalSub, humanize, jvPath, options, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const REPORTS = [
  { key: 'register', labelKey: 'prepaidRegister' },
  { key: 'schedule', labelKey: 'recognitionSchedule' },
  { key: 'movement', labelKey: 'prepaidMovement' },
  { key: 'forecast', labelKey: 'recognitionForecast' },
] as const;

const ReportShell: React.FC<{ active: string; extra?: React.ReactNode; children: React.ReactNode }> = ({ active, extra, children }) => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const access = useRecognitionAccess();
  if (!access.canView) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('noAccessPrepaidReports')} />;
  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid')}>
            {t('workspace')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('prepaidReports')}
          </Title>
        </Space>
        {extra}
      </Space>
      <Tabs activeKey={active} onChange={(k) => navigate(`/fin/prepaid/reports/${k}`)} items={REPORTS.map((r) => ({ key: r.key, label: t(r.labelKey) }))} />
      {children}
    </div>
  );
};

const ExportButtons: React.FC<{ name: string; columns: ReportColumnDef[]; rows: Record<string, unknown>[] }> = ({
  name,
  columns,
  rows,
}) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  return (
    <Space>
      <Button icon={<DownloadOutlined />} disabled={!rows.length} onClick={() => downloadCsv(name, columns, rows)}>
        {t('csv')}
      </Button>
      <Button icon={<DownloadOutlined />} disabled={!rows.length} onClick={() => void downloadXlsx(name, columns, rows)}>
        {tc('excel')}
      </Button>
    </Space>
  );
};

const col = (key: string, title: string): ReportColumnDef => ({ key, title, dataIndex: key });

function useLookups() {
  const { data: categories = [] } = useQuery({ queryKey: ['recognition', 'categories'], queryFn: () => recognitionService.listCategories() });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-prepaid'],
    queryFn: async () => unwrap<any[]>(await api.get('/procurement/suppliers')) ?? [],
  });
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: projects = [] } = useProjectsForSelection();
  return {
    categoryOptions: categories.map((c) => ({ value: c.id, label: c.name })),
    supplierOptions: suppliers.map((s: any) => ({ value: s.id, label: s.supplier_name })),
    assetOptions: assetAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })),
    expenseOptions: expenseAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) })),
    projectOptions: (projects as any[]).map((p) => ({ value: p.id, label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name })),
  };
}

const sel = (placeholder: string, value: string | undefined, opts: { value: string; label: string }[], onChange: (v?: string) => void, width = 200) => (
  <Select allowClear showSearch optionFilterProp="label" placeholder={placeholder} style={{ width }} value={value} options={opts} onChange={onChange} />
);

// ---------------------------------------------------------------- Register
export const PrepaidRegisterReportPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const lk = useLookups();
  const [f, setF] = useState<Record<string, string | undefined>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const set = (patch: Record<string, string | undefined>) => {
    setF((p) => ({ ...p, ...patch }));
    setPage(1);
  };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'report', 'register', f, page, pageSize],
    queryFn: () => recognitionService.registerReport({ ...f, page, limit: pageSize, sort: 'recognition_number', order: 'ASC' }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.data ?? [];
  const exportCols: ReportColumnDef[] = [
    col('recognition_number', 'Prepaid number'),
    col('supplier_name', 'Supplier'),
    col('category_name', 'Category'),
    col('description', 'Description'),
    col('source_document_number', 'Source invoice'),
    col('service_start_date', 'Service start'),
    col('service_end_date', 'Service end'),
    col('recognizable_amount', 'Original amount'),
    col('recognized_amount', 'Recognised amount'),
    col('remaining_amount', 'Remaining amount'),
    col('transaction_currency', 'Currency'),
    col('remaining_base_amount', 'Remaining (base)'),
    col('prepaid_account_code', 'Prepaid GL'),
    col('expense_account_code', 'Expense GL'),
    col('status', 'Status'),
  ];
  return (
    <ReportShell active="register" extra={<ExportButtons name="prepaid-register" columns={exportCols} rows={rows as unknown as Record<string, unknown>[]} />}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search allowClear placeholder={tc('search')} style={{ width: 220 }} onSearch={(v) => set({ search: v || undefined })} />
          <Select
            mode="multiple"
            allowClear
            placeholder={tc('status')}
            style={{ minWidth: 200 }}
            options={options(CONTRACT_STATUSES)}
            onChange={(v: string[]) => set({ status: v.length ? v.join(',') : undefined })}
          />
          {sel('Supplier', f.supplier_id, lk.supplierOptions, (v) => set({ supplier_id: v }))}
          {sel('Category', f.category_id, lk.categoryOptions, (v) => set({ category_id: v }), 170)}
          {sel('Prepaid GL', f.prepaid_asset_account_id, lk.assetOptions, (v) => set({ prepaid_asset_account_id: v }), 220)}
          {sel('Expense GL', f.expense_account_id, lk.expenseOptions, (v) => set({ expense_account_id: v }), 220)}
          {sel('Project', f.project_id, lk.projectOptions, (v) => set({ project_id: v }), 220)}
          <DatePicker.RangePicker
            placeholder={['Service end from', 'to']}
            onChange={(r) => set({ service_end_from: r?.[0]?.format('YYYY-MM-DD'), service_end_to: r?.[1]?.format('YYYY-MM-DD') })}
          />
        </Space>
      </Card>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 1900 }}
        pagination={{ current: page, pageSize, total: data?.total ?? 0, showSizeChanger: true, pageSizeOptions: [50, 100, 200, 500], onChange: (p, s) => { setPage(p); setPageSize(s); } }}
        columns={[
          { title: t('prepaidNumber'), dataIndex: 'recognition_number', width: 150, fixed: 'left', render: (v: string, r) => <Link to={contractPath(r.id)}>{v}</Link> },
          { title: t('supplier'), dataIndex: 'supplier_name', width: 180, ellipsis: true },
          { title: t('category'), dataIndex: 'category_name', width: 140 },
          { title: t('description'), dataIndex: 'description', width: 220, ellipsis: true },
          { title: t('sourceInvoice'), dataIndex: 'source_document_number', width: 140, render: (v: string | null, r) => v || humanize(r.source_type) },
          { title: t('serviceStart'), dataIndex: 'service_start_date', width: 110 },
          { title: t('serviceEnd'), dataIndex: 'service_end_date', width: 110 },
          { title: t('original'), dataIndex: 'recognizable_amount', width: 130, align: 'right', render: (v: string) => amount(v) },
          { title: t('recognised'), dataIndex: 'recognized_amount', width: 130, align: 'right', render: (v: string) => amount(v) },
          { title: t('remaining'), dataIndex: 'remaining_amount', width: 130, align: 'right', render: (v: string) => amount(v) },
          { title: t('currency'), dataIndex: 'transaction_currency', width: 80 },
          { title: t('remainingBase'), dataIndex: 'remaining_base_amount', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('prepaidGl'), key: 'pa', width: 180, ellipsis: true, render: (_: unknown, r) => accountLabel(r.prepaid_account_code, r.prepaid_account_name) },
          { title: t('expenseGl'), key: 'ea', width: 180, ellipsis: true, render: (_: unknown, r) => accountLabel(r.expense_account_code, r.expense_account_name) },
          { title: tc('status'), dataIndex: 'status', width: 140, render: (s: string) => <StatusTag status={s} /> },
        ]}
        summary={() =>
          data?.totals ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={11}>
                  <Text strong>
                    {data.totals.contract_count} contracts (base currency): recognisable {amount(data.totals.recognizable_base_amount)} · recognised{' '}
                    {amount(data.totals.recognized_base_amount)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  <Text strong>{amount(data.totals.remaining_base_amount)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12} colSpan={3} />
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </ReportShell>
  );
};

// ---------------------------------------------------------------- Schedule
export const PrepaidScheduleReportPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const lk = useLookups();
  const [searchParams] = useSearchParams();
  const [f, setF] = useState<Record<string, string | undefined>>(() => {
    const initial: Record<string, string | undefined> = {
      from_period: searchParams.get('from_period') || dayjs().format('YYYY-MM'),
      to_period: searchParams.get('to_period') || dayjs().add(11, 'month').format('YYYY-MM'),
    };
    for (const k of ['category_id', 'supplier_id', 'expense_account_id', 'prepaid_asset_account_id', 'status']) {
      const v = searchParams.get(k);
      if (v) initial[k] = v;
    }
    return initial;
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const set = (patch: Record<string, string | undefined>) => {
    setF((p) => ({ ...p, ...patch }));
    setPage(1);
  };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'report', 'schedule', f, page, pageSize],
    queryFn: () => recognitionService.scheduleReport({ ...f, page, limit: pageSize }),
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(
    () =>
      (data?.data ?? []).map((r) => ({
        ...r,
        variance: decimalSub(r.planned_base_amount, r.posted_base_amount),
      })),
    [data]
  );
  const exportCols: ReportColumnDef[] = [
    col('recognition_number', 'Contract'),
    col('supplier_name', 'Supplier'),
    col('expense_account_code', 'Expense GL'),
    col('accounting_period_key', 'Accounting period'),
    col('recognition_from_date', 'Recognition from'),
    col('recognition_to_date', 'Recognition to'),
    col('recognition_days', 'Recognition days'),
    col('planned_base_amount', 'Planned (base)'),
    col('posted_base_amount', 'Posted (base)'),
    col('variance', 'Variance'),
    col('posting_date', 'Posting date'),
    col('journal_voucher_number', 'JV number'),
    col('status', 'Status'),
  ];
  return (
    <ReportShell active="schedule" extra={<ExportButtons name="recognition-schedule" columns={exportCols} rows={rows as unknown as Record<string, unknown>[]} />}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <DatePicker.RangePicker
            picker="month"
            value={[f.from_period ? dayjs(`${f.from_period}-01`) : null, f.to_period ? dayjs(`${f.to_period}-01`) : null]}
            onChange={(r) => set({ from_period: r?.[0]?.format('YYYY-MM'), to_period: r?.[1]?.format('YYYY-MM') })}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder={t('lineStatus')}
            style={{ minWidth: 220 }}
            value={f.status ? f.status.split(',') : []}
            options={options(LINE_STATUSES)}
            onChange={(v: string[]) => set({ status: v.length ? v.join(',') : undefined })}
          />
          {sel('Supplier', f.supplier_id, lk.supplierOptions, (v) => set({ supplier_id: v }))}
          {sel('Category', f.category_id, lk.categoryOptions, (v) => set({ category_id: v }), 170)}
          {sel('Prepaid GL', f.prepaid_asset_account_id, lk.assetOptions, (v) => set({ prepaid_asset_account_id: v }), 220)}
          {sel('Expense GL', f.expense_account_id, lk.expenseOptions, (v) => set({ expense_account_id: v }), 220)}
        </Space>
      </Card>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}
      {data?.by_period && data.by_period.length > 0 && (
        <Card size="small" title={t('byAccountingPeriodBase')} style={{ marginBottom: 12 }}>
          <Table
            size="small"
            rowKey="accounting_period_key"
            pagination={false}
            dataSource={data.by_period}
            columns={[
              { title: t('period'), dataIndex: 'accounting_period_key', width: 110 },
              { title: t('scheduled'), dataIndex: 'scheduled_base', align: 'right', render: (v: string) => amount(v) },
              { title: tc('posted'), dataIndex: 'posted_base', align: 'right', render: (v: string) => amount(v) },
              { title: t('open'), dataIndex: 'open_base', align: 'right', render: (v: string) => amount(v) },
            ]}
          />
        </Card>
      )}
      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 1600 }}
        pagination={{ current: page, pageSize, total: data?.total ?? 0, showSizeChanger: true, pageSizeOptions: [100, 200, 500, 1000], onChange: (p, s) => { setPage(p); setPageSize(s); } }}
        columns={[
          { title: t('contract'), dataIndex: 'recognition_number', width: 140, render: (v: string, r) => <Link to={`${contractPath(r.recognition_contract_id)}?tab=schedule`}>{v}</Link> },
          { title: t('supplier'), dataIndex: 'supplier_name', width: 170, ellipsis: true },
          { title: t('expenseGl'), key: 'ea', width: 170, ellipsis: true, render: (_: unknown, r) => accountLabel(r.expense_account_code, r.expense_account_name) },
          { title: t('period'), dataIndex: 'accounting_period_key', width: 90 },
          { title: t('from'), dataIndex: 'recognition_from_date', width: 105 },
          { title: t('to'), dataIndex: 'recognition_to_date', width: 105 },
          { title: t('days'), dataIndex: 'recognition_days', width: 60, align: 'right' },
          { title: t('plannedBase'), dataIndex: 'planned_base_amount', width: 130, align: 'right', render: (v: string) => amount(v) },
          { title: t('postedBase'), dataIndex: 'posted_base_amount', width: 130, align: 'right', render: (v: string) => amount(v) },
          { title: t('variance'), dataIndex: 'variance', width: 110, align: 'right', render: (v: string) => amount(v) },
          { title: t('postingDate'), dataIndex: 'posting_date', width: 110 },
          { title: t('jv'), key: 'jv', width: 140, render: (_: unknown, r) => (r.journal_voucher_id ? <Link to={jvPath(r.journal_voucher_id)}>{r.journal_voucher_number}</Link> : '—') },
          { title: tc('status'), dataIndex: 'status', width: 140, render: (s: string) => <StatusTag status={s} /> },
        ]}
      />
    </ReportShell>
  );
};

// ---------------------------------------------------------------- Movement
const MOVEMENT_GROUPS = [
  { value: 'contract', labelKey: 'contract' },
  { value: 'account', labelKey: 'prepaidAccount' },
  { value: 'category', labelKey: 'category' },
  { value: 'supplier', labelKey: 'supplier' },
  { value: 'project', labelKey: 'project' },
  { value: 'cost_center', labelKey: 'costCentre' },
] as const;

export const PrepaidMovementReportPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const lk = useLookups();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const [f, setF] = useState<Record<string, string | undefined>>({ group_by: 'account' });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'report', 'movement', range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'), f],
    queryFn: () => recognitionService.movementReport({ ...f, from_date: range[0].format('YYYY-MM-DD'), to_date: range[1].format('YYYY-MM-DD') }),
  });
  const rows = data?.rows ?? [];
  const groupKey = MOVEMENT_GROUPS.find((g) => g.value === (f.group_by ?? 'contract'))?.labelKey;
  const groupLabel = groupKey ? t(groupKey) : t('group');
  const exportCols: ReportColumnDef[] = [
    col('key_code', t('code')),
    col('key_name', groupLabel),
    col('opening', t('openingPrepaid')),
    col('additions', t('newPrepaymentsAdditions')),
    col('recognized', t('recognition')),
    col('refunds', t('refunds')),
    col('write_offs', t('writeOffs')),
    col('termination_expense', t('terminationExpense')),
    col('source_reversals', t('sourceReversals')),
    col('closing', t('closingPrepaid')),
  ];
  const money = (v: string) => amount(v);
  const groupOptions = MOVEMENT_GROUPS.map((g) => ({ value: g.value, label: t(g.labelKey) }));
  return (
    <ReportShell active="movement" extra={<ExportButtons name="prepaid-movement" columns={exportCols} rows={rows as unknown as Record<string, unknown>[]} />}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <DatePicker.RangePicker value={range} allowClear={false} onChange={(r) => r?.[0] && r?.[1] && setRange([r[0], r[1]])} />
          <Select style={{ width: 180 }} value={f.group_by} options={groupOptions} onChange={(v) => setF((p) => ({ ...p, group_by: v }))} />
          {sel(t('prepaidGl'), f.prepaid_asset_account_id, lk.assetOptions, (v) => setF((p) => ({ ...p, prepaid_asset_account_id: v })), 220)}
          {sel(t('supplier'), f.supplier_id, lk.supplierOptions, (v) => setF((p) => ({ ...p, supplier_id: v })))}
          {sel(t('category'), f.category_id, lk.categoryOptions, (v) => setF((p) => ({ ...p, category_id: v })), 170)}
          {sel(t('project'), f.project_id, lk.projectOptions, (v) => setF((p) => ({ ...p, project_id: v })), 220)}
          <Input allowClear placeholder={t('costCentre')} style={{ width: 140 }} onChange={(e) => setF((p) => ({ ...p, cost_center: e.target.value || undefined }))} />
        </Space>
      </Card>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}
      <Text type="secondary">
        {t('prepaidMovementFormula')}
      </Text>
      <Table
        style={{ marginTop: 8 }}
        size="small"
        rowKey="key_id"
        loading={isLoading}
        dataSource={rows}
        scroll={{ x: 1500 }}
        pagination={{ pageSize: 100 }}
        columns={[
          {
            title: groupLabel,
            key: 'key',
            width: 260,
            fixed: 'left',
            render: (_: unknown, r) =>
              (f.group_by ?? 'contract') === 'contract' ? (
                <Link to={contractPath(r.key_id)}>
                  {r.key_code} {r.key_name}
                </Link>
              ) : (
                [r.key_code, r.key_name].filter(Boolean).join(' - ')
              ),
          },
          { title: t('opening'), dataIndex: 'opening', align: 'right', render: money },
          { title: t('plusNewAdditions'), dataIndex: 'additions', align: 'right', render: money },
          { title: t('minusRecognition'), dataIndex: 'recognized', align: 'right', render: money },
          { title: t('minusRefunds'), dataIndex: 'refunds', align: 'right', render: money },
          { title: t('minusWriteOffs'), dataIndex: 'write_offs', align: 'right', render: money },
          { title: t('minusTermination'), dataIndex: 'termination_expense', align: 'right', render: money },
          { title: t('plusMinusSourceReversals'), dataIndex: 'source_reversals', align: 'right', render: money },
          { title: t('equalsClosing'), dataIndex: 'closing', align: 'right', render: (v: string) => <Text strong>{amount(v)}</Text> },
        ]}
        summary={() =>
          data ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>{t('total')}</Text>
                </Table.Summary.Cell>
                {(['opening', 'additions', 'recognized', 'refunds', 'write_offs', 'termination_expense', 'source_reversals', 'closing'] as const).map((k, i) => (
                  <Table.Summary.Cell key={k} index={i + 1} align="right">
                    <Text strong>{amount(data.totals[k])}</Text>
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </ReportShell>
  );
};

// ---------------------------------------------------------------- Forecast
const periodLabel = (p: string) => (p === 'OVERDUE' ? 'Overdue' : p === 'LATER' ? 'Later' : dayjs(`${p}-01`).format('MMM YYYY'));

export const PrepaidForecastReportPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const lk = useLookups();
  const [asOf, setAsOf] = useState(dayjs());
  const [months, setMonths] = useState(12);
  const [groupBy, setGroupBy] = useState<'month' | 'expense_account' | 'category' | 'supplier'>('month');
  const [f, setF] = useState<Record<string, string | undefined>>({});
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'report', 'forecast', asOf.format('YYYY-MM-DD'), months, groupBy, f],
    queryFn: () => recognitionService.forecastReport({ ...f, as_of: asOf.format('YYYY-MM-DD'), months, group_by: groupBy }),
  });

  const periods = data?.periods ?? [];
  const monthly = groupBy === 'month';
  const monthlyRows = periods.map((p) => ({ period: p, label: periodLabel(p), amount: data?.totals[p] ?? '0.00' }));
  const dimRows = (data?.rows ?? []).map((r) => ({ dim_id: r.dim_id, label: r.label, total: r.total, ...r.values }));

  const exportCols: ReportColumnDef[] = monthly
    ? [col('label', 'Accounting period'), col('amount', 'Recognition (base)')]
    : [col('label', humanize(groupBy)), ...periods.map((p) => ({ key: p, title: periodLabel(p), dataIndex: p })), col('total', 'Total')];

  const drillFrom = (p: string) => (p === 'OVERDUE' || p === 'LATER' ? undefined : p);
  const scheduleLink = (p: string, extra = '') =>
    `/fin/prepaid/reports/schedule?from_period=${drillFrom(p) ?? ''}&to_period=${drillFrom(p) ?? ''}&status=PLANNED,DUE,HELD,POSTING_BLOCKED,DRAFT_JV_CREATED${extra}`;

  return (
    <ReportShell
      active="forecast"
      extra={<ExportButtons name="recognition-forecast" columns={exportCols} rows={(monthly ? monthlyRows : dimRows) as unknown as Record<string, unknown>[]} />}
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <DatePicker value={asOf} allowClear={false} onChange={(v) => v && setAsOf(v)} />
          <InputNumber min={1} max={60} value={months} addonAfter="months" onChange={(v) => setMonths(Number(v) || 12)} />
          <Select
            style={{ width: 200 }}
            value={groupBy}
            onChange={setGroupBy}
            options={[
              { value: 'month', label: t('byPeriodOnly') },
              { value: 'expense_account', label: t('byExpenseAccount') },
              { value: 'category', label: t('byCategory') },
              { value: 'supplier', label: t('bySupplier') },
            ]}
          />
          {sel('Prepaid GL', f.prepaid_asset_account_id, lk.assetOptions, (v) => setF((p) => ({ ...p, prepaid_asset_account_id: v })), 220)}
          {sel('Category', f.category_id, lk.categoryOptions, (v) => setF((p) => ({ ...p, category_id: v })), 170)}
        </Space>
      </Card>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}
      <Text type="secondary">Open schedule lines of live contracts, base currency. Grand total {amount(data?.grand_total)}.</Text>
      {monthly ? (
        <Table
          style={{ marginTop: 8, maxWidth: 520 }}
          size="small"
          rowKey="period"
          loading={isLoading}
          dataSource={monthlyRows}
          pagination={false}
          columns={[
            {
              title: t('accountingPeriod'),
              dataIndex: 'label',
              render: (v: string, r) => (drillFrom(r.period) ? <Link to={scheduleLink(r.period)}>{v}</Link> : v),
            },
            { title: t('recognitionBase'), dataIndex: 'amount', align: 'right', render: (v: string) => amount(v) },
          ]}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text strong>{t('total')}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong>{amount(data?.grand_total)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      ) : (
        <Table
          style={{ marginTop: 8 }}
          size="small"
          rowKey="dim_id"
          loading={isLoading}
          dataSource={dimRows}
          pagination={false}
          scroll={{ x: 240 + periods.length * 120 }}
          columns={[
            { title: humanize(groupBy), dataIndex: 'label', width: 240, fixed: 'left', ellipsis: true },
            ...periods.map((p) => ({
              title: periodLabel(p),
              dataIndex: p,
              width: 120,
              align: 'right' as const,
              render: (v: string, r: { dim_id: string }) => {
                const extra = groupBy === 'category' && r.dim_id !== 'NONE' ? `&category_id=${r.dim_id}` : groupBy === 'supplier' && r.dim_id !== 'NONE' ? `&supplier_id=${r.dim_id}` : groupBy === 'expense_account' ? `&expense_account_id=${r.dim_id}` : '';
                return Number(v) && drillFrom(p) ? <Link to={scheduleLink(p, extra)}>{amount(v)}</Link> : amount(v);
              },
            })),
            { title: t('total'), dataIndex: 'total', width: 130, align: 'right', fixed: 'right', render: (v: string) => <Text strong>{amount(v)}</Text> },
          ]}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Text strong>{t('total')}</Text>
                </Table.Summary.Cell>
                {periods.map((p, i) => (
                  <Table.Summary.Cell key={p} index={i + 1} align="right">
                    <Text strong>{amount(data?.totals[p])}</Text>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={periods.length + 1} align="right">
                  <Text strong>{amount(data?.grand_total)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
    </ReportShell>
  );
};
